import { getValidAccessToken, logout, showAlertOnce, hasStoredAuthUser } from "./Auth";

/**
 * A wrapper around native fetch that transparently handles 401 Unauthorized responses:
 * - Detects 401 responses.
 * - Refreshes the Cognito session token via getValidAccessToken().
 * - Re-attempts the fetch once with the updated Authorization header.
 * - Logs out and alerts the user if session refresh fails or retry returns 401 again.
 */
export async function authFetch(
  input: RequestInfo | URL,
  init?: RequestInit,
  retryCount: number = 0
): Promise<Response> {
  const response = await fetch(input, init);

  if (response.status === 401 && retryCount === 0) {
    try {
      const freshToken = await getValidAccessToken();
      if (freshToken) {
        const headers = new Headers(init?.headers);
        headers.set("Authorization", `Bearer ${freshToken}`);

        return authFetch(input, { ...init, headers }, retryCount + 1);
      }
    } catch (refreshErr) {
      console.error("[authFetch] Token refresh failed:", refreshErr);
    }

    if (hasStoredAuthUser()) {
      logout();
      showAlertOnce();
    }
  } else if (response.status === 401 && retryCount > 0) {
    if (hasStoredAuthUser()) {
      logout();
      showAlertOnce();
    }
  }

  return response;
}
